import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
  UserKindEnum,
} from '../../../types';

import { normalizeAdsFilteringObject } from './normalizeAdsFilteringObject';
import { getAdsApiFilter, parseAdsSearchParams } from './adsSearchParams';

export interface AppliedAdsFilterSnapshotContext {
  readonly pathname: string;
  readonly search: string;
}

export interface AppliedAdsFilterLocationSnapshot {
  addressEn?: string;
  coordinatesLat?: string;
  coordinatesLong?: string;
  nameEn?: string;
  placeId?: string;
}

export interface AppliedAdsFilterCreatorSnapshot {
  id?: string;
  kind?: UserKindEnum;
  name?: string;
}

export interface AppliedAdsFilterHandoverSnapshot {
  quarter?: string;
  year?: string;
}

export interface AppliedAdsFilterSnapshot {
  activityKind: ActivityKindEnum;
  transactionType?: TransactionTypeEnum[];
  rentFrequency?: RentPeriodEnum;
  locationId?: string[];
  location?: AppliedAdsFilterLocationSnapshot;
  propertyKind?: PropertyKindEnum[];
  beds?: AdFieldBedsEnum[];
  baths?: AdFieldBathsEnum[];
  priceMin?: number;
  priceMax?: number;
  areaMin?: number;
  areaMax?: number;
  completionStatus?: CompletionStatusEnum[];
  furniture?: AdFieldFurnitureEnum[];
  renovation?: AdFieldRenovationEnum[];
  floor?: AdFieldFloorEnum[];
  floorsNumFrom?: number;
  floorsNumTo?: number;
  occupancy?: AdFieldOccupancyEnum[];
  handover?: AppliedAdsFilterHandoverSnapshot;
  multiHandover?: string[];
  postHandover?: boolean;
  downPayment?: number;
  creator?: AppliedAdsFilterCreatorSnapshot;
  isMyAgency?: boolean;
  myFriends?: boolean;
  keywords?: string;
}

type MeaningfulAppliedFilterKey = Exclude<
  keyof AppliedAdsFilterSnapshot,
  'activityKind' | 'isMyAgency' | 'myFriends'
>;

const meaningfulAppliedFilterKeys = [
  'transactionType',
  'rentFrequency',
  'locationId',
  'location',
  'propertyKind',
  'beds',
  'baths',
  'priceMin',
  'priceMax',
  'areaMin',
  'areaMax',
  'completionStatus',
  'furniture',
  'renovation',
  'floor',
  'floorsNumFrom',
  'floorsNumTo',
  'occupancy',
  'handover',
  'multiHandover',
  'postHandover',
  'downPayment',
  'creator',
  'keywords',
] as const satisfies readonly MeaningfulAppliedFilterKey[];

const isRentStorefrontPath = (pathname: string) => pathname.replace(/\/+$/, '') === '/ads/rent';

const hasProperties = (value: object) => Object.keys(value).length > 0;

const hasMeaningfulSnapshotValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(hasMeaningfulSnapshotValue);
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(hasMeaningfulSnapshotValue);
  }

  return false;
};

export const hasMeaningfulAppliedFilters = (snapshot: AppliedAdsFilterSnapshot): boolean =>
  snapshot.isMyAgency === true ||
  snapshot.myFriends === true ||
  meaningfulAppliedFilterKeys.some((key) =>
    key === 'postHandover'
      ? snapshot.postHandover !== undefined
      : hasMeaningfulSnapshotValue(snapshot[key])
  );

export const buildAppliedAdsFilterSnapshot = ({
  pathname,
  search,
}: AppliedAdsFilterSnapshotContext): AppliedAdsFilterSnapshot => {
  const searchParams = parseAdsSearchParams(search);
  const normalizedFilter = normalizeAdsFilteringObject(
    getAdsApiFilter(searchParams, isRentStorefrontPath(pathname))
  );
  const locationId = searchParams.locationId?.length ? [...searchParams.locationId] : undefined;
  const hasAppliedLocation = !!locationId || !!normalizedFilter.address?.length;
  const location: AppliedAdsFilterLocationSnapshot = {
    ...(searchParams.addressParamAddress ? { addressEn: searchParams.addressParamAddress } : {}),
    ...(searchParams.addressParamLat ? { coordinatesLat: searchParams.addressParamLat } : {}),
    ...(searchParams.addressParamLong ? { coordinatesLong: searchParams.addressParamLong } : {}),
    ...(searchParams.addressParamName ? { nameEn: searchParams.addressParamName } : {}),
    ...(searchParams.addressParamPlaceId ? { placeId: searchParams.addressParamPlaceId } : {}),
  };
  const creator = normalizedFilter.creator
    ? {
        ...(normalizedFilter.creator.id ? { id: normalizedFilter.creator.id } : {}),
        ...(searchParams['creator.kind'] ? { kind: searchParams['creator.kind'] } : {}),
        ...(normalizedFilter.creator.name ? { name: normalizedFilter.creator.name } : {}),
      }
    : undefined;
  const handover = normalizedFilter.handover
    ? {
        ...(normalizedFilter.handover.quarter
          ? { quarter: normalizedFilter.handover.quarter }
          : {}),
        ...(normalizedFilter.handover.year ? { year: normalizedFilter.handover.year } : {}),
      }
    : undefined;

  return {
    activityKind: normalizedFilter.activityKind as ActivityKindEnum,
    ...(searchParams.transactionType?.length
      ? { transactionType: [...searchParams.transactionType] }
      : {}),
    ...(normalizedFilter.rentFrequency ? { rentFrequency: normalizedFilter.rentFrequency } : {}),
    ...(locationId ? { locationId } : {}),
    ...(hasAppliedLocation && hasProperties(location) ? { location } : {}),
    ...(searchParams.propertyKind?.length ? { propertyKind: [...searchParams.propertyKind] } : {}),
    ...(searchParams.beds?.length ? { beds: [...searchParams.beds] } : {}),
    ...(searchParams.baths?.length ? { baths: [...searchParams.baths] } : {}),
    ...(searchParams.priceMin !== undefined ? { priceMin: searchParams.priceMin } : {}),
    ...(searchParams.priceMax !== undefined ? { priceMax: searchParams.priceMax } : {}),
    ...(searchParams.areaMin !== undefined ? { areaMin: searchParams.areaMin } : {}),
    ...(searchParams.areaMax !== undefined ? { areaMax: searchParams.areaMax } : {}),
    ...(normalizedFilter.completionStatus && searchParams.completionStatus?.length
      ? { completionStatus: [...searchParams.completionStatus] }
      : {}),
    ...(searchParams.furniture?.length ? { furniture: [...searchParams.furniture] } : {}),
    ...(searchParams.renovation?.length ? { renovation: [...searchParams.renovation] } : {}),
    ...(searchParams.floor?.length ? { floor: [...searchParams.floor] } : {}),
    ...(searchParams.floorsNumFrom !== undefined
      ? { floorsNumFrom: searchParams.floorsNumFrom }
      : {}),
    ...(searchParams.floorsNumTo !== undefined ? { floorsNumTo: searchParams.floorsNumTo } : {}),
    ...(searchParams.occupancy?.length ? { occupancy: [...searchParams.occupancy] } : {}),
    ...(handover && hasProperties(handover) ? { handover } : {}),
    ...(searchParams.multiHandover?.length
      ? { multiHandover: [...searchParams.multiHandover] }
      : {}),
    ...(searchParams.postHandover !== undefined ? { postHandover: searchParams.postHandover } : {}),
    ...(searchParams.downPayment !== undefined ? { downPayment: searchParams.downPayment } : {}),
    ...(creator && hasProperties(creator) ? { creator } : {}),
    ...(searchParams.isMyAgency !== undefined ? { isMyAgency: searchParams.isMyAgency } : {}),
    ...(normalizedFilter.myFriends !== undefined ? { myFriends: normalizedFilter.myFriends } : {}),
    ...(searchParams.keywords ? { keywords: searchParams.keywords } : {}),
  };
};
